import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, ViewChild } from "@angular/core";
import { Observable, of, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { SecurityGroupSecurityModel } from "src/app/core/models/security-model/security-group-security.model";
import { ActivityStoreService } from "src/app/core/utils/activity-store.service";
import { PagingDataView } from "src/app/core/views/pagging-data.view";
import { Helper } from "src/app/shared/helper/app.helper";
import { LoadingSpinnerService } from "src/app/shared/layout/loading-spinner";
import { ActionBarHandlerModel, ActionbarService, ACTION_STATUS } from "src/app/shared/ui/actionbar";
import { CancelButtonModel, CopyButtonModel, NewButtonModel, RefreshButtonModel } from "src/app/shared/ui/actionbar/models";
import { StatusConstant } from "src/app/shared/ui/forms/inputs/status-color-dropdown/shared/constants/status.constant";
import { NavigationService } from "src/app/shared/utils/navigation";
import { PriceService } from "../prices/shared/price.service";
import { PricingConstant } from "../pricing-content/shared/constants/pricing.constant";
import { DetailsComponent } from "./details/details.component";
import { SearchConverter } from "./shared/search.converter";
import { SearchConditionView, SearchResultView } from "./shared/views";

@Component({
    selector: 'op-price-dimensions',
    templateUrl: './price-dimensions.component.html'
})

export class PriceDimensionsComponent implements AfterViewInit, OnDestroy {
    private readonly defaultUsageTypeCode = 'PRICEDIMENTEMPLATE';
    priceId: string;
    copyMode: boolean;
    searchConditionView = new SearchConditionView(this.defaultUsageTypeCode);

    newBtn = new NewButtonModel();
    copyBtn = new CopyButtonModel();
    defaultActionbarHandler: ActionBarHandlerModel;
    searchResultViews$ = new Observable<SearchResultView[]>();

    @Input() executeFlag: boolean = true;
    @Input() userSecurity: SecurityGroupSecurityModel;
    @ViewChild(DetailsComponent) detailsComponent: DetailsComponent;

    unsubscribe$ = new Subject();

    private pagingDataViews: PagingDataView[] = [];

    constructor(protected helper: Helper,
        private priceService: PriceService,
        private converter: SearchConverter,
        private cdr: ChangeDetectorRef,
        private actionbarService: ActionbarService,
        private spinnerService: LoadingSpinnerService,
        private navigationService: NavigationService,
        private activityStoreService: ActivityStoreService) {
    }

    ngAfterViewInit(): void {
        this.setupActionbarSecurity();
        this.actionbarService.updateState(this.defaultActionbarHandler);
        this.actionbarService.action$.pipe(takeUntil(this.unsubscribe$)).subscribe(
            actionId => {
                switch (actionId) {
                    case ACTION_STATUS.refresh:
                        this.searchPriceDimensions(this.searchConditionView);
                        break;
                    case ACTION_STATUS.new:
                        this.newPriceDimensions();
                        break;
                    default:
                        break;
                }
            }
        )
        if (this.executeFlag == true) {
            this.searchPriceDimensions(this.searchConditionView);
        }
        this.activityStore();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.unsubscribe();
    }

    setupActionbarSecurity() {
        this.newBtn.enable(this.userSecurity?.newFlag ?? false);
        this.copyBtn.enable(this.userSecurity?.copyFlag ?? false);

        this.defaultActionbarHandler = new ActionBarHandlerModel(
            this.newBtn,
            this.copyBtn,
            new CancelButtonModel(),
            new RefreshButtonModel()
        )

    }

    searchPriceDimensions(view: SearchConditionView) {
        this.spinnerService.show();
        this.searchConditionView = view;
        this.searchResultViews$ = this.priceService
            .search(this.converter.toModel(view))
            .pipe(map(models => {
                this.spinnerService.hide();
                this.pagingDataViews = this.converter.toPagingDataViews(models);
                return this.converter.toViews(models)
            }))
    }

    newPriceDimensions() {
        this.goToPriceDimensionsDetail(null);
    }

    clearSearchCondition() {
        this.searchConditionView = new SearchConditionView(this.defaultUsageTypeCode);
        this.searchResultViews$ = of([]);
    }

    editPriceDimensions(priceId: string) {
        this.goToPriceDimensionsDetail(priceId);
    }

    goToPriceDimensionsDetail(priceId: string) {
        this.navigationService.navigate(`/main/pricing/dimension/details`, null, null, {
            priceId: priceId,
            previousPage: PricingConstant.dimensions.id,
            pagingDataViews: priceId ? this.pagingDataViews : [],
            userSecurity: this.userSecurity
        });
    }

    deletePriceDimensions(priceId: string) {
        if (priceId) {
            this.spinnerService.showSaving();
            this.priceService.updateStatus(priceId, StatusConstant.DELETED)
                .subscribe(
                    () => { 
                        this.spinnerService.saveComplete();
                        this.searchPriceDimensions(this.searchConditionView);
                    },
                    () => {
                    },
                    () => {
                        this.spinnerService.hide();
                        this.cdr.detectChanges();
                    }
                )
        }
    }

    copyPriceDimensions(priceId: string) {
        this.copyMode = true;
        this.editPriceDimensions(priceId);
    }

    onPriceDimensionDetailsLoadCompleted() {
        if (this.copyMode) {
            this.detailsComponent.initializeCopyMode();
        }
    }

    refreshResult() {
        this.searchPriceDimensions(this.searchConditionView);
    }

    private activityStore() {
        this.activityStoreService.add(
            null,
            null,
            null,
            null,
            null,
            null,
            "PRICEDIMENSION");
    }
}